import { Divider, Card } from "semantic-ui-react";
import { TextTruncate } from "components/textTruncators/TextTruncators";
import { Tooltip } from "components/common/tooltip";
import { type PaymentPack, type PurchasePrice, type PaymentFormData } from "../../types";
import dateTimeUtils from "../../../../../../utils/dateTimeUtils";
import { Typography, type TypographyVariants } from "../../../../../../components/common/Typography/Typography";
import { Fragment, type ReactNode } from "react";
import IntrinsicElements = React.JSX.IntrinsicElements;
import moment from "moment";

import styles from "./OrderSummary.module.scss";
import cn from "classnames";
import { withUSFormattedNumberInput } from "../../../../../../components/forms/USFormattedNumberInput/USFormattedNumberInput";
import InputField from "../../../../../../components/forms/InputField/InputField";
import { type FormikProps } from "formik";
import { useFeatureFlag } from "../../../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../../../featureFlags";

interface Props {
  packs: PaymentPack[];
  purchasePrice: PurchasePrice;
  formik?: FormikProps<PaymentFormData>;
}
const USFormattedInputField = withUSFormattedNumberInput(InputField);

const getDisplayExpirationDate = (pack: PaymentPack): Date | undefined => {
  if (!pack.expirationDate) {
    return undefined;
  }

  if (pack.isExpired && pack.billingTerm === "Annual") {
    return moment().add(12, "months").toDate();
  }

  return pack.expirationDate;
};

const OrderSummary = ({ packs, purchasePrice, formik }: Props) => {
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);
  const formatCurrency = (value: number) => currencyFormatter.format(value);

  const renderLicensesField = (pack: PaymentPack, index: number) =>
    additionalPurchaseEnabled && formik ? (
      <USFormattedInputField
        {...formik}
        className={styles.licensesCountField}
        propertyName={`packs[${index}].licensesCount`}
        value={formik.values.packs?.[index]?.licensesCount || null}
        onValueChanged={(data: { value: number | null }) =>
          formik.setFieldValue(`packs[${index}].licensesCount`, data.value)
        }
        markAsRequired
        errorPosition="left"
      />
    ) : (
      pack.licensesCount?.toLocaleString("en-US")
    );

  const renderPackDetails = (pack: PaymentPack, index: number) => {
    const pricePerUserPerMonth = pack.rawPricePerUser / (pack.billingTerm === "Annual" ? 12 : 1);
    const displayExpirationDate = getDisplayExpirationDate(pack);

    return (
      <Fragment key={pack.packId}>
        {renderRow("Pack", pack.packName, { bold: true })}
        {renderRow(additionalPurchaseEnabled ? "Licenses" : "User Licenses", renderLicensesField(pack, index), {
          bold: true,
        })}
        {renderRow(
          "Price/user/month",
          renderTooltipValue(formatCurrency(pricePerUserPerMonth), exactPriceFormatter(pricePerUserPerMonth)),
          { bold: true },
        )}
        {renderRow("Billing term", pack.billingTerm, { bold: true })}
        {displayExpirationDate &&
          renderRow("Expires", dateTimeUtils.formatDate(displayExpirationDate), {
            bold: true,
          })}
        <Divider />
      </Fragment>
    );
  };

  return (
    <Card className={styles.root} fluid>
      <Card.Content>
        <Typography variant="h4" className={styles.header}>
          Order Details
        </Typography>

        <div className={styles.content}>
          {packs.map(renderPackDetails)}
          {renderRow(
            "Subtotal",
            <TextTruncate aria-label="subtotal">{formatCurrency(purchasePrice.subtotalAmount)}</TextTruncate>,
            { tinted: true },
          )}
          {renderRow("Taxes", <TextTruncate aria-label="tax">{formatCurrency(purchasePrice.taxAmount)}</TextTruncate>, {
            tinted: true,
          })}
          <Divider />
          {renderRow(
            "Total",
            <TextTruncate aria-label="total">{formatCurrency(purchasePrice.totalAmount)}</TextTruncate>,
            { bold: true, component: "span", variant: "h4" },
          )}
        </div>
      </Card.Content>
    </Card>
  );
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const exactPriceFormatter = (value: number) =>
  `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })}`;

const renderTooltipValue = (shortValue: string | number, fullValue: string | number) => (
  <Tooltip target={<TextTruncate>{shortValue}</TextTruncate>} content={String(fullValue)} hideOnScroll showAlways />
);

interface RenderRowProps {
  bold?: boolean;
  tinted?: boolean;
  variant?: TypographyVariants;
  component?: keyof IntrinsicElements;
}
const renderRow = (
  label: string,
  content: ReactNode,
  { bold = false, tinted = false, variant = "title2", component }: RenderRowProps = {},
) => (
  <div className={styles.item}>
    <Typography variant={variant} className={cn({ [styles.tinted]: tinted })}>
      {label}
    </Typography>
    <Typography variant={variant} component={component} bold={bold}>
      {content}
    </Typography>
  </div>
);

export default OrderSummary;
